import type { Maybe } from '@custom/dx3';
import { useParamHandler } from '@custom/dx3';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MissionCategoryDisplayValue } from '../../assets/constants/missionFiltering';
import { categories } from '../../assets/constants/missionFiltering';
import type { GetMissionSectionDataQueryVariables } from '../../models/__generated__/graphql';
import { MissionFlag } from '../../models/__generated__/graphql';
import { areParamsEqual } from '../routingHelpers';

const parseTextToBool = (str: string) => {
  return str.toLowerCase() === 'true'
    ? true
    : str.toLowerCase() === 'false'
    ? false
    : null;
};

const getMissionFlagFromString = (str: Maybe<string>) => {
  if (str && Object.values(MissionFlag).includes(str as MissionFlag)) {
    return str as MissionFlag;
  }

  // default is Eligible
  return MissionFlag.Eligible;
};

export function useMissionsFilter() {
  const navigate = useNavigate();

  const onNavigate = (str: string) => navigate(str, { replace: true });

  const [categoryParam, setCategoryParam] = useParamHandler(
    'category',
    onNavigate
  );
  const [isLikedParam, setIsLikedParam] = useParamHandler('liked', onNavigate);
  const [flagParam, setFlagParam] = useParamHandler('flag', onNavigate);

  const [filterVariables, setFilterVariables] =
    useState<GetMissionSectionDataQueryVariables>({
      flag: MissionFlag.Featured,
      labels: null,
      expiringSoon: null,
      inProgress: null,
      repeatable: null,
      iLike: null,
    });

  // if an invalid param has been set remove it.
  useEffect(() => {
    const catQueryParams = categories.map((cat) => decodeURI(cat.queryParam));
    if (categoryParam && !catQueryParams.includes(categoryParam)) {
      setCategoryParam(null);
    }
  }, [categoryParam, setCategoryParam]);

  useEffect(() => {
    const parsed = isLikedParam
      ? parseTextToBool(isLikedParam)?.toString()
      : null;
    if (isLikedParam && parsed === null) {
      setIsLikedParam(null);
    }
  }, [isLikedParam, setIsLikedParam]);

  useEffect(() => {
    const parsed =
      flagParam && Object.values(MissionFlag).includes(flagParam as MissionFlag)
        ? (flagParam as MissionFlag)
        : null;
    if (flagParam && parsed === null) {
      setFlagParam(MissionFlag.Featured);
    }
  }, [flagParam, setFlagParam]);

  const isLikedAsBool = useMemo(() => {
    return isLikedParam ? parseTextToBool(isLikedParam) : null;
  }, [isLikedParam]);

  useEffect(() => {
    const categoriesVars = categories.find((cat) =>
      areParamsEqual(cat.queryParam, categoryParam)
    )?.variables;

    const combinedVars: Partial<GetMissionSectionDataQueryVariables> = {
      flag: getMissionFlagFromString(flagParam),
      ...categoriesVars,
      iLike: isLikedAsBool,
    };

    setFilterVariables({
      labels: null,
      expiringSoon: null,
      inProgress: null,
      repeatable: null,
      flag: MissionFlag.Eligible,
      iLike: null,
      ...combinedVars,
    });
  }, [categoryParam, isLikedAsBool, flagParam]);

  const setCategory = useCallback(
    (value: MissionCategoryDisplayValue) => {
      const foundParam = categories.find(
        (cat) => cat.displayValue === value
      )?.queryParam;

      // This is || not ?? because when the found queryParam is '' we want to remove it
      setCategoryParam(foundParam || null);
    },
    [setCategoryParam]
  );

  const setIsLiked = useCallback(
    (value: 'true' | 'false' | null) => {
      setIsLikedParam(value || null);
    },
    [setIsLikedParam]
  );

  return {
    // return the display value not the query parameter value
    category:
      categories.find((cat) => areParamsEqual(cat.queryParam, categoryParam))
        ?.displayValue ?? 'All',
    setCategory,
    filterVariables,
    isLiked: isLikedAsBool,
    setIsLiked,
    flagParam: getMissionFlagFromString(flagParam),
    setFlagParam,
  } as {
    category: MissionCategoryDisplayValue;
    setCategory: (value: MissionCategoryDisplayValue) => void;
    filterVariables: GetMissionSectionDataQueryVariables;
    isLiked: boolean | null;
    setIsLiked: (value: 'true' | 'false' | null) => void;
    flagParam: MissionFlag;
    setFlagParam: (value: string) => void;
  };
}
